import { ServiceSessionModel } from "./ServiceSessionModel"
import { SellerServiceDetail } from "../../domain/entities/SellerServiceDetail"
import { CategoryModel } from "./CategoryModel"
import { CalendarAvailabilityModel } from "./CalendarAvailabilityModel"
import { ServiceTranslationModel } from "./ServiceTranslationModel"
import { SellersModel } from "./SellersModel"
import { ServicePicture } from "../../domain/entities/ServicePicture"
import { ServiceTranslation } from "../../domain/entities/ServiceTranslation"
import { ServiceStatus } from "../../domain/enum/serviceStatus.enum"
import { ServiceOfficeModel } from "./ServiceOfficeModel"
import { ServiceEventType } from "../../domain/enum/serviceEventType.enum"

interface ServiceMoreInformation {
	title?: string
	description?: string
	footer?: string
}

interface ContructorParams {
	serviceId: string
	title: string
	hint?: string
	description?: string
	price?: number
	typeService?: string
	picture?: string
	isOnline?: boolean
	isPresential?: boolean
	isFoundation?: boolean
	serviceClusters: CategoryModel[]
	weight: number
	serviceSessions: ServiceSessionModel[]
	serviceTranslations?: ServiceTranslationModel[]
	serviceContent?: { pictures: ServicePicture[] }[]
	endorsedUser?: { id: string; name: string } | null
	seller?: SellersModel
	countryId?: string
	highlighted?: boolean
	crossSelling?: boolean
	status: ServiceStatus
	askForInformation?: boolean
	offices?: ServiceOfficeModel[]
	hasWaitlist: boolean
	moreInformation?: ServiceMoreInformation | null
	cancellationHours?: number | null
	isForSurvey?: boolean
	consecutiveReservations: boolean
	isTournament: boolean
	finishTournamentDate?: Date | null
	availableTournamentPlaces?: number | null
	participants?: number | null
	eventDateTime?: Date | null
	prizes?: string
	place?: string
	estimatedDuration?: number | null
	googleMapsUrl?: string
	eventType?: ServiceEventType
	endDateTime?: Date | null
}

export class SellerServicesDetailModel {
	public serviceId: string
	public title: string
	public hint?: string
	public description?: string
	public price?: number
	public typeService?: string
	public picture?: string
	public isOnline?: boolean
	public isPresential?: boolean
	public isFoundation?: boolean
	public serviceClusters: CategoryModel[]
	public serviceContent?: { pictures: ServicePicture[] }[]
	public weight: number
	public serviceSessions: ServiceSessionModel[]
	public endorsedUser?: { id: string; name: string } | null
	public countryId?: string
	public highlighted?: boolean
	public serviceTranslations: ServiceTranslationModel[]
	public seller?: SellersModel
	public crossSelling: boolean
	public status: ServiceStatus
	public askForInformation?: boolean
	public offices?: ServiceOfficeModel[]
	public hasWaitlist: boolean
	public moreInformation?: ServiceMoreInformation | null
	public cancellationHours?: number | null
	public isForSurvey?: boolean
	public consecutiveReservations: boolean
	public isTournament: boolean
	public finishTournamentDate?: Date | null
	public availableTournamentPlaces?: number | null
	public participants?: number | null
	public eventDateTime?: Date | null
	public prizes?: string
	public place?: string
	public googleMapsUrl?: string
	public eventType?: ServiceEventType
	public estimatedDuration?: number | null
	public endDateTime?: Date | null

	constructor(params: ContructorParams) {
		this.serviceId = params.serviceId
		this.title = params.title
		this.hint = params.hint
		this.description = params.description
		this.price = params.price
		this.typeService = params.typeService
		this.picture = params.picture
		this.isOnline = params.isOnline
		this.isPresential = params.isPresential
		this.isFoundation = params.isFoundation
		this.serviceClusters = params.serviceClusters
		this.weight = params.weight
		this.serviceSessions = params.serviceSessions
		this.endorsedUser = params.endorsedUser
		this.countryId = params.countryId
		this.highlighted = params.highlighted
		this.serviceTranslations = params.serviceTranslations ?? []
		this.seller = params.seller
		this.serviceContent = params.serviceContent
		this.crossSelling = params.crossSelling ?? false
		this.status = params.status
		this.askForInformation = params.askForInformation
		this.offices = params.offices
		this.hasWaitlist = params.hasWaitlist
		this.moreInformation = params.moreInformation
		this.cancellationHours = params.cancellationHours
		this.isForSurvey = params.isForSurvey
		this.consecutiveReservations = params.consecutiveReservations
		this.isTournament = params.isTournament
		this.finishTournamentDate = params.finishTournamentDate
		this.availableTournamentPlaces = params.availableTournamentPlaces
		this.participants = params.participants
		this.eventDateTime = params.eventDateTime
		this.prizes = params.prizes
		this.place = params.place
		this.estimatedDuration = params.estimatedDuration
		this.googleMapsUrl = params.googleMapsUrl
		this.eventType = params.eventType
		this.endDateTime = params.endDateTime
	}

	public get title_en() {
		return this.serviceTranslations?.find(t => t.locale === "en-us")?.title
	}
	public get title_ca() {
		return this.serviceTranslations?.find(t => t.locale === "ca-es")?.title
	}
	public get title_es() {
		return this.serviceTranslations?.find(t => t.locale === "es-es")?.title
	}
	public get description_en() {
		return this.serviceTranslations?.find(t => t.locale === "en-us")?.description
	}
	public get description_ca() {
		return this.serviceTranslations?.find(t => t.locale === "ca-es")?.description
	}
	public get description_es() {
		return this.serviceTranslations?.find(t => t.locale === "es-es")?.description
	}
	public get hint_en() {
		return this.serviceTranslations?.find(t => t.locale === "en-us")?.hint
	}
	public get hint_ca() {
		return this.serviceTranslations?.find(t => t.locale === "ca-es")?.hint
	}
	public get hint_es() {
		return this.serviceTranslations?.find(t => t.locale === "es-es")?.hint
	}

	public static fromRawJson = (rawJson: any): SellerServicesDetailModel => {
		return new SellerServicesDetailModel({
			...rawJson,
			serviceId: rawJson.id,
			seller: SellersModel.fromRawJson(rawJson.seller),
			serviceClusters: rawJson.serviceClusters?.map((category: any) => CategoryModel.fromRawJson(category)),
			availability: rawJson.availability?.map((slot: any) => CalendarAvailabilityModel.fromRawJson(slot)),
			serviceSessions: rawJson.serviceSessions?.map((session: any) => ServiceSessionModel.fromRawJson(session)),
			serviceTranslations: rawJson.serviceTranslations?.map((t: any) => ServiceTranslationModel.fromRawJson(t)),
			serviceContent: rawJson.serviceContent?.map((content: any) => ({
				pictures: content.pictures?.filter((p: any) => !p.cover)
			})),
			offices: rawJson.offices?.map((o: any) => ServiceOfficeModel.fromRawJson(o))
		})
	}

	public static fromDomain = (service: SellerServiceDetail): SellerServicesDetailModel => {
		const serviceTranslations = [
			new ServiceTranslation({
				locale: "es-es",
				title: service.title,
				description: service.description,
				serviceId: service.id
			})
		]
		if (service.title_en || service.description_en || service.hint_en) {
			serviceTranslations.push(SellerServicesDetailModel.getTranslation(service, "en-us", "en"))
		}
		if (service.title_ca || service.description_ca || service.hint_ca) {
			serviceTranslations.push(SellerServicesDetailModel.getTranslation(service, "ca-es", "ca"))
		}
		return new SellerServicesDetailModel({
			...service,
			serviceId: service.id,
			typeService: service.typeService,
			seller: service.seller ? SellersModel.fromDomain(service.seller) : undefined,
			price: service.price || 0,
			isFoundation: service.isFoundation || false,
			isOnline: service.isOnline || false,
			isPresential: service.isPresential || false,
			serviceClusters: service.serviceClusters?.map(category => CategoryModel.fromDomain(category)),
			serviceSessions: service.serviceSessions?.map(session => ServiceSessionModel.fromDomain(session)),
			countryId: service.countryId,
			serviceTranslations: serviceTranslations.map(t => ServiceTranslationModel.fromDomain(t)),
			endorsedUser:
				service.endorsedName && service.endorsedUserId
					? { name: service.endorsedName, id: service.endorsedUserId }
					: null,
			offices: service.offices?.map(o => ServiceOfficeModel.fromDomain(o)),
			cancellationHours: service.cancellationHours,
			isForSurvey: service.isForSurvey
		})
	}

	private static getTranslation(service: SellerServiceDetail, locale: string, lang: "en" | "ca"): ServiceTranslation {
		return new ServiceTranslation({
			locale,
			title: service[`title_${lang}`],
			description: service[`description_${lang}`],
			hint: service[`hint_${lang}`],
			serviceId: service.id
		})
	}
}

declare module "./SellerServicesDetailModel" {
	interface SellerServicesDetailModel {
		toDomain(): SellerServiceDetail
	}
}

SellerServicesDetailModel.prototype.toDomain = function (): SellerServiceDetail {
	const data = this
	return new SellerServiceDetail({
		...data,
		title_en: data.title_en,
		title_ca: data.title_ca,
		description_en: data.description_en,
		description_ca: data.description_ca,
		hint_en: data.hint_en,
		hint_ca: data.hint_ca,
		id: data.serviceId,
		seller: data.seller?.toDomain(),
		typeService: data.typeService || "",
		isFoundation: data.isFoundation,
		weight: data.weight,
		isOnline: data.isOnline,
		isPresential: data.isPresential,
		serviceClusters: data.serviceClusters?.map(category => category.toDomain()),
		serviceSessions: data.serviceSessions?.map(session =>
			session.toDomain(data.offices?.find(o => o.id === session.serviceOfficeId))
		),
		endorsedUser: data.endorsedUser,
		countryId: data.countryId,
		endorsedName: data.endorsedUser?.name,
		endorsedUserId: data.endorsedUser?.id,
		endorsedUsername: data.endorsedUser?.name,
		offices: data.offices?.map(o => o.toDomain()),
		cancellationHours: data.cancellationHours,
		isForSurvey: data.isForSurvey
	})
}
